import React from "react";
import { useTranslation } from "react-i18next";

/**
 * LiveIndicator Component
 * Shows websocket connection status with visual states
 */
const STATUS = {
  CONNECTED: "connected",
  RECONNECTING: "reconnecting",
  OFFLINE: "offline",
};

const resolveStatus = ({ connectionState, isConnected }) => {
  if (connectionState === STATUS.CONNECTED) return STATUS.CONNECTED;
  if (connectionState === STATUS.RECONNECTING) return STATUS.RECONNECTING;
  if (connectionState === STATUS.OFFLINE) return STATUS.OFFLINE;
  return isConnected ? STATUS.CONNECTED : STATUS.OFFLINE;
};

const LiveIndicator = ({
  isConnected = false,
  connectionState,
  reconnectAttempt = 0,
  maxReconnectAttempts = 20,
  className = "",
}) => {
  const { t } = useTranslation("navigation");
  const status = resolveStatus({ connectionState, isConnected });
  const currentAttempt = Math.max(1, reconnectAttempt || 1);

  const statusConfig = {
    [STATUS.CONNECTED]: {
      dotClass: "bg-green-400",
      textClass: "text-green-400",
      showPing: true,
      label: t("topbar.connectionLive"),
    },
    [STATUS.RECONNECTING]: {
      dotClass: "bg-amber-400",
      textClass: "text-amber-400",
      showPing: false,
      label: t("topbar.connectionReconnecting", {
        current: currentAttempt,
        total: maxReconnectAttempts,
      }),
    },
    [STATUS.OFFLINE]: {
      dotClass: "bg-red-400",
      textClass: "text-red-400",
      showPing: false,
      label: t("topbar.connectionOffline"),
    },
  };

  const { dotClass, textClass, showPing, label } = statusConfig[status];

  return (
    <div
      className={`inline-flex items-center space-x-2 rounded-full bg-gray-800 px-3 py-1.5 ${className}`}
    >
      {/* Pulsing dot indicator */}
      <div className="relative">
        <div className={`h-2 w-2 rounded-full ${dotClass}`} />
        {showPing && (
          <div
            className={`absolute inset-0 h-2 w-2 animate-ping rounded-full ${dotClass} opacity-75`}
          />
        )}
      </div>

      {/* Live text */}
      <span className={`text-xs font-medium ${textClass}`}>{label}</span>
    </div>
  );
};

export default LiveIndicator;
